import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/api";
import { Logo } from "@/components/brand/Logo";
import {
  LayoutDashboard, Flame, Church, Users, Building2, Sparkles, Mic, PlayCircle,
  Image, Video, Megaphone, Calendar, MessageSquareQuote, HandHeart, HeartHandshake,
  Mail, Settings as SettingsIcon, LogOut, UserCog, ExternalLink,
} from "lucide-react";

const GROUPS = [
  { title: "General", items: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  ] },
  { title: "Ministry", roles: ["super_admin", "ministry_admin", "church_admin"], items: [
    { to: "/admin/ministry", label: "Ministry", icon: Flame, roles: ["super_admin", "ministry_admin"] },
    { to: "/admin/church", label: "Church", icon: Church, roles: ["super_admin", "ministry_admin", "church_admin"] },
    { to: "/admin/leadership", label: "Leadership", icon: Users, roles: ["super_admin", "ministry_admin"] },
    { to: "/admin/departments", label: "Departments", icon: Building2, roles: ["super_admin", "ministry_admin", "department_manager"] },
    { to: "/admin/evangelism-activities", label: "Evangelism", icon: Sparkles, roles: ["super_admin", "ministry_admin"] },
  ] },
  { title: "Content", items: [
    { to: "/admin/sermons", label: "Sermons", icon: PlayCircle },
    { to: "/admin/podcasts", label: "Podcasts", icon: Mic },
    { to: "/admin/gallery", label: "Gallery", icon: Image },
    { to: "/admin/videos", label: "Videos", icon: Video },
    { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
    { to: "/admin/events", label: "Events", icon: Calendar },
    { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
    { to: "/admin/campaigns", label: "Campaigns", icon: Sparkles, roles: ["super_admin", "ministry_admin"] },
    { to: "/admin/statistics", label: "Statistics", icon: LayoutDashboard, roles: ["super_admin", "ministry_admin"] },
  ] },
  { title: "Ministry Ops", roles: ["super_admin", "ministry_admin", "followup_team"], items: [
    { to: "/admin/followups", label: "Follow-Up", icon: HeartHandshake, roles: ["super_admin", "ministry_admin", "followup_team"] },
    { to: "/admin/counsellors", label: "Counsellors", icon: Users, roles: ["super_admin", "ministry_admin", "followup_team"] },
    { to: "/admin/partners", label: "Church Partners", icon: Building2, roles: ["super_admin", "ministry_admin"] },
  ] },
  { title: "Engagement", roles: ["super_admin", "ministry_admin", "church_admin", "followup_team"], items: [
    { to: "/admin/prayer-requests", label: "Prayer Requests", icon: HandHeart, roles: ["super_admin", "ministry_admin", "church_admin", "followup_team"] },
    { to: "/admin/giving", label: "Giving", icon: HeartHandshake, roles: ["super_admin", "ministry_admin"] },
    { to: "/admin/contact-messages", label: "Messages", icon: Mail, roles: ["super_admin", "ministry_admin", "church_admin"] },
  ] },
  { title: "System", roles: ["super_admin"], items: [
    { to: "/admin/users", label: "Users & Roles", icon: UserCog, roles: ["super_admin"] },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin"] },
  ] },
];

const canSee = (roles, role) => !roles || roles.includes(role);

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: ministry } = useFetch("/ministry");
  const role = user?.role;

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-secondary/40">
      <aside className="fixed inset-y-0 left-0 flex w-64 flex-col border-r border-white/10 bg-navy">
        <div className="border-b border-white/10 p-5">
          <Logo variant="light" src={ministry?.logo} />
        </div>
        <nav className="no-scrollbar flex-1 overflow-y-auto px-3 py-4">
          {GROUPS.filter((g) => canSee(g.roles, role)).map((g) => {
            const items = g.items.filter((it) => canSee(it.roles, role));
            if (!items.length) return null;
            return (
              <div key={g.title} className="mb-5">
                <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">{g.title}</p>
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${
                        isActive ? "bg-brand-orange text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                      }`
                    }
                  >
                    <it.icon className="h-4 w-4" /> {it.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3">
          <a href="/" target="_blank" rel="noreferrer" className="mb-2 flex items-center gap-3 rounded-sm px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white">
            <ExternalLink className="h-4 w-4" /> View Website
          </a>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="ml-64 flex-1">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-white px-8">
          <span className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-navy">Admin Dashboard</span>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-navy">{user?.name}</p>
              <p className="text-xs capitalize text-muted-foreground">{role?.replace("_", " ")}</p>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-navy font-display text-sm font-bold text-white">
              {user?.name?.[0]?.toUpperCase() || "A"}
            </div>
          </div>
        </header>
        <main className="p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
